/**
 * useDashboardWebSocket — Centralized WebSocket event listeners for dashboard pages.
 *
 * Subscribes to real-time events and invalidates the relevant React Query caches,
 * enabling instant dashboard updates without manual refresh.
 *
 * Events handled:
 * - production-session-created/updated: Session and station data
 * - station-updated: Station status changes
 * - inventory-updated: Lot and inventory changes
 * - box-packed/cancelled/deleted: Finished product updates
 * - waste-recorded: Production waste events
 * - worker-clocked-in/out: Workforce changes
 * - schedule-updated: Manpower schedule changes
 * - hte-transfer-updated/created: Transfer workflow events
 * - pending-label-voided: Label management events
 */
import { useEffect, useState, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { wsService } from '../services/websocket';

export interface DashboardWebSocketOptions {
  /** Enable production session events (default: true) */
  enableProductionEvents?: boolean;
  /** Enable inventory events (default: true) */
  enableInventoryEvents?: boolean;
  /** Enable workforce events (default: true) */
  enableWorkforceEvents?: boolean;
  /** Enable transfer/approval events (default: true) */
  enableTransferEvents?: boolean;
  /** Callback when any event is received (for debugging or logging) */
  onEvent?: (eventName: string, data: any) => void;
}

export interface DashboardWebSocketState {
  /** Whether WebSocket is connected */
  isConnected: boolean;
  /** Last event received (for debugging) */
  lastEvent: { name: string; timestamp: Date } | null;
}

/**
 * Hook for real-time dashboard updates via WebSocket.
 *
 * @param options - Configuration options
 * @returns Connection state
 *
 * @example
 * ```tsx
 * const { isConnected } = useDashboardWebSocket();
 *
 * return (
 *   <div>
 *     {isConnected && <span className="text-green-500">Live</span>}
 *   </div>
 * );
 * ```
 */
export function useDashboardWebSocket(options: DashboardWebSocketOptions = {}): DashboardWebSocketState {
  const {
    enableProductionEvents = true,
    enableInventoryEvents = true,
    enableWorkforceEvents = true,
    enableTransferEvents = true,
    onEvent,
  } = options;

  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<{ name: string; timestamp: Date } | null>(null);

  // Helper to track events
  const trackEvent = useCallback((eventName: string, data: any) => {
    setLastEvent({ name: eventName, timestamp: new Date() });
    onEvent?.(eventName, data);
  }, [onEvent]);

  useEffect(() => {
    // Ensure WebSocket is connected
    wsService.connect();

    // Track connection state
    const handleConnect = () => setIsConnected(true);
    const handleDisconnect = () => setIsConnected(false);

    const invalidateProductionViews = (sessionNumber?: string | number | null) => {
      queryClient.invalidateQueries(['productionSessions']);
      queryClient.invalidateQueries(['all-production-sessions']);
      queryClient.invalidateQueries(['supervisor-stations']);
      queryClient.invalidateQueries(['stations']);
      queryClient.invalidateQueries(['production-progress']);
      queryClient.invalidateQueries(['end-of-day-summary']);
      if (sessionNumber != null) {
        const normalizedSessionNumber = String(sessionNumber);
        queryClient.invalidateQueries(['session-details', normalizedSessionNumber]);
        queryClient.invalidateQueries(['sub-sessions', normalizedSessionNumber]);
        queryClient.invalidateQueries(['session-history', normalizedSessionNumber]);
      }
    };

    // Production session events
    const handleSessionCreated = (data: any) => {
      trackEvent('production-session-created', data);
      invalidateProductionViews(data?.session_number);
      queryClient.invalidateQueries(['operators']);
    };

    const handleSessionUpdated = (data: any) => {
      trackEvent('production-session-updated', data);
      invalidateProductionViews(data?.session_number);
      queryClient.invalidateQueries(['operators']);
      // Operator dashboard queries
      queryClient.invalidateQueries({ queryKey: ['operator-session'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['operator-stats'], exact: false });
    };

    const handleStationUpdated = (data: any) => {
      trackEvent('station-updated', data);
      queryClient.invalidateQueries(['supervisor-stations']);
      queryClient.invalidateQueries(['stations']);
      queryClient.invalidateQueries(['productionSessions']);
    };

    const handleSessionQueued = (data: any) => {
      trackEvent('production-session-queued', data);
      queryClient.invalidateQueries(['supervisor-stations']);
      queryClient.invalidateQueries(['productionSessions']);
    };

    const handleSessionUnqueued = (data: any) => {
      trackEvent('production-session-unqueued', data);
      queryClient.invalidateQueries(['supervisor-stations']);
      queryClient.invalidateQueries(['productionSessions']);
    };

    // Inventory events
    const handleInventoryUpdated = (data: any) => {
      trackEvent('inventory-updated', data);
      queryClient.invalidateQueries(['lots']);
      queryClient.invalidateQueries(['lots-summary']);
      queryClient.invalidateQueries(['finished-products-summary']);
      queryClient.invalidateQueries(['finished-products-grouped']);
    };

    const handleBoxPacked = (data: any) => {
      trackEvent('box-packed', data);
      invalidateProductionViews(data?.session_number);
      queryClient.invalidateQueries(['finished-products-summary']);
      queryClient.invalidateQueries(['finished-products-grouped']);
      // Operator dashboard queries (boxes affect output stats)
      queryClient.invalidateQueries({ queryKey: ['operator-session'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['operator-stats'], exact: false });
    };

    const handleBoxCancelled = (data: any) => {
      trackEvent('box-cancelled', data);
      invalidateProductionViews(data?.session_number);
      queryClient.invalidateQueries(['finished-products-summary']);
    };

    const handleBoxDeleted = (data: any) => {
      trackEvent('box-deleted', data);
      invalidateProductionViews(data?.session_number);
      queryClient.invalidateQueries(['finished-products-summary']);
    };

    const handleWasteRecorded = (data: any) => {
      trackEvent('waste-recorded', data);
      invalidateProductionViews(data?.session_number);
      queryClient.invalidateQueries(['lots-summary']);
      queryClient.invalidateQueries(['kpi-byproduct-summary']);
    };

    // Workforce events
    const handleWorkerClockedIn = (data: any) => {
      trackEvent('worker-clocked-in', data);
      queryClient.invalidateQueries(['manpower-summary']);
      queryClient.invalidateQueries(['operators']);
      queryClient.invalidateQueries(['supervisor-stations']);
      if (data?.session_number) {
        queryClient.invalidateQueries(['session-history', data.session_number]);
        queryClient.invalidateQueries(['sub-sessions', data.session_number]);
      }
    };

    const handleWorkerClockedOut = (data: any) => {
      trackEvent('worker-clocked-out', data);
      queryClient.invalidateQueries(['manpower-summary']);
      queryClient.invalidateQueries(['operators']);
      queryClient.invalidateQueries(['supervisor-stations']);
      if (data?.session_number) {
        queryClient.invalidateQueries(['session-history', data.session_number]);
        queryClient.invalidateQueries(['sub-sessions', data.session_number]);
      }
    };

    const handleScheduleUpdated = (data: any) => {
      trackEvent('schedule-updated', data);
      queryClient.invalidateQueries(['manpower-summary']);
      queryClient.invalidateQueries(['work-schedule']);
    };

    // Transfer/approval events
    const handleHteTransferUpdated = (data: any) => {
      trackEvent('hte-transfer-updated', data);
      queryClient.invalidateQueries(['pending-approvals']);
      queryClient.invalidateQueries(['lots-summary']);
      queryClient.invalidateQueries(['lots']);
    };

    const handleHteTransferCreated = (data: any) => {
      trackEvent('hte-transfer-created', data);
      queryClient.invalidateQueries(['pending-approvals']);
      queryClient.invalidateQueries(['lots-summary']);
      queryClient.invalidateQueries(['lots']);
    };

    const handleDailyTransferOutUpdated = (data: any) => {
      trackEvent('daily-transfer-out-updated', data);
      queryClient.invalidateQueries(['daily-transfer-out']);
    };

    const handleDailyTransferOutFinalized = (data: any) => {
      trackEvent('daily-transfer-out-finalized', data);
      queryClient.invalidateQueries(['daily-transfer-out']);
      queryClient.invalidateQueries(['lots-summary']);
    };

    const handlePendingLabelVoided = (data: any) => {
      trackEvent('pending-label-voided', data);
      invalidateProductionViews(data?.session_number);
    };

    // Register connection handlers
    wsService.on('connect', handleConnect);
    wsService.on('disconnect', handleDisconnect);

    // Register event handlers based on options
    if (enableProductionEvents) {
      wsService.on('production-session-created', handleSessionCreated);
      wsService.on('production-session-updated', handleSessionUpdated);
      wsService.on('station-updated', handleStationUpdated);
      wsService.on('production-session-queued', handleSessionQueued);
      wsService.on('production-session-unqueued', handleSessionUnqueued);
    }

    if (enableInventoryEvents) {
      wsService.on('inventory-updated', handleInventoryUpdated);
      wsService.on('box-packed', handleBoxPacked);
      wsService.on('box-cancelled', handleBoxCancelled);
      wsService.on('box-deleted', handleBoxDeleted);
      wsService.on('waste-recorded', handleWasteRecorded);
      wsService.on('pending-label-voided', handlePendingLabelVoided);
    }

    if (enableWorkforceEvents) {
      wsService.on('worker-clocked-in', handleWorkerClockedIn);
      wsService.on('worker-clocked-out', handleWorkerClockedOut);
      wsService.on('schedule-updated', handleScheduleUpdated);
    }

    if (enableTransferEvents) {
      wsService.on('hte-transfer-updated', handleHteTransferUpdated);
      wsService.on('hte-transfer-created', handleHteTransferCreated);
      wsService.on('daily-transfer-out-updated', handleDailyTransferOutUpdated);
      wsService.on('daily-transfer-out-finalized', handleDailyTransferOutFinalized);
    }

    // Cleanup on unmount
    return () => {
      wsService.off('connect', handleConnect);
      wsService.off('disconnect', handleDisconnect);

      if (enableProductionEvents) {
        wsService.off('production-session-created', handleSessionCreated);
        wsService.off('production-session-updated', handleSessionUpdated);
        wsService.off('station-updated', handleStationUpdated);
        wsService.off('production-session-queued', handleSessionQueued);
        wsService.off('production-session-unqueued', handleSessionUnqueued);
      }

      if (enableInventoryEvents) {
        wsService.off('inventory-updated', handleInventoryUpdated);
        wsService.off('box-packed', handleBoxPacked);
        wsService.off('box-cancelled', handleBoxCancelled);
        wsService.off('box-deleted', handleBoxDeleted);
        wsService.off('waste-recorded', handleWasteRecorded);
        wsService.off('pending-label-voided', handlePendingLabelVoided);
      }

      if (enableWorkforceEvents) {
        wsService.off('worker-clocked-in', handleWorkerClockedIn);
        wsService.off('worker-clocked-out', handleWorkerClockedOut);
        wsService.off('schedule-updated', handleScheduleUpdated);
      }

      if (enableTransferEvents) {
        wsService.off('hte-transfer-updated', handleHteTransferUpdated);
        wsService.off('hte-transfer-created', handleHteTransferCreated);
        wsService.off('daily-transfer-out-updated', handleDailyTransferOutUpdated);
        wsService.off('daily-transfer-out-finalized', handleDailyTransferOutFinalized);
      }
    };
  }, [
    queryClient,
    trackEvent,
    enableProductionEvents,
    enableInventoryEvents,
    enableWorkforceEvents,
    enableTransferEvents,
  ]);

  return { isConnected, lastEvent };
}

export default useDashboardWebSocket;
